import {
  ConstraintEffect,
  ParameterConfigurationType,
  SlideConfig,
  SlideInput,
  SlideRequest
} from '@blisspointmedia/bpm-types/dist/SlidesV2';
import { evaluateConstraints } from '@blisspointmedia/bpm-types/dist/SlidesV2Rules';
import { isRecord } from '@src/utils/type-utils';

export const SlideSource = {
  ORIGINAL: 'original',
  PRESET_LOOP: 'preset-loop',
  LOCAL_DUPLICATE: 'local-duplicate'
} as const;
export type SlideSource = (typeof SlideSource)[keyof typeof SlideSource];

interface BaseSlideInstance extends SlideConfig {
  key: string;
  slideNumber: number;
  originalSlideId: string; // Always points to the base slide
  localId?: string; // For local duplicates
  isSoftDeleted?: boolean; // For soft-deleted duplicated slides in preset
}

export type SlideInstance =
  | (BaseSlideInstance & {
      source: typeof SlideSource.ORIGINAL | typeof SlideSource.PRESET_LOOP;
      duplicateOfKey?: undefined;
    })
  | (BaseSlideInstance & {
      source: typeof SlideSource.LOCAL_DUPLICATE;
      duplicateOfKey: string; // The template key whose saved values it copies
    });

export type Edits = Record<string, Record<string, unknown>>;

/**
 * Returns the edited value for a parameter, else its saved value.
 * Uses "key in edits" so that an explicit clear (undefined) overrides the saved value.
 */
export const resolveParameterValue = (
  key: string,
  slideEdits: Record<string, unknown> = {},
  slideInitValues: Record<string, unknown> = {}
): unknown => (key in slideEdits ? slideEdits[key] : slideInitValues[key]);

/**
 * Merges edits on top of saved values to get the full current state for constraint evaluation.
 */
export const getAllCurrentValues = (
  slideEdits: Record<string, unknown> = {},
  slideInitValues: Record<string, unknown> = {}
): Record<string, unknown> => ({ ...slideInitValues, ...slideEdits });

export const isOptionAllowed = (
  value: string,
  allowed: string[] | undefined,
  disallowed: string[] | undefined
): boolean => (!allowed || allowed.includes(value)) && !disallowed?.includes(value);

export const isMetricAllowed = (metric: string, effects: ConstraintEffect): boolean =>
  isOptionAllowed(
    metric,
    effects.allowedMetricConfigOptions?.metrics,
    effects.disallowedMetricConfigOptions?.metrics
  );

export const isSourceAllowed = (source: string, effects: ConstraintEffect): boolean =>
  isOptionAllowed(
    source,
    effects.allowedMetricConfigOptions?.sources,
    effects.disallowedMetricConfigOptions?.sources
  );

/**
 * True when the parameter's current value has an option that its active constraints reject:
 * the values autoClearInvalidValues clears. Does not check whether the parameter is hidden.
 */
export const hasRejectedValue = (
  parameter: SlideInput,
  allCurrentValues: Record<string, unknown>
): boolean => {
  const value = allCurrentValues[parameter.key];
  if (!parameter.constraints?.length || !value) {
    return false;
  }

  const effects = evaluateConstraints(parameter.constraints, allCurrentValues);
  switch (parameter.type) {
    case ParameterConfigurationType.METRIC_CONFIG_DROPDOWN:
    case ParameterConfigurationType.METRIC_CONFIG_WITHOUT_SOURCE_DROPDOWN: {
      if (typeof value !== 'object') {
        return false;
      }
      const metric = 'metric' in value && typeof value.metric === 'string' ? value.metric : '';
      const source = 'source' in value && typeof value.source === 'string' ? value.source : '';
      return (
        (Boolean(metric) && !isMetricAllowed(metric, effects)) ||
        (Boolean(source) && !isSourceAllowed(source, effects))
      );
    }
    case ParameterConfigurationType.SINGLE_SELECT_DROPDOWN:
      return (
        typeof value === 'string' &&
        !isOptionAllowed(value, effects.allowedValues, effects.disallowedValues)
      );
    case ParameterConfigurationType.MULTI_SELECT_DROPDOWN:
      return (
        Array.isArray(value) &&
        value.some(
          v =>
            !parameter.options.some(opt => opt.value === v) ||
            !isOptionAllowed(v, effects.allowedValues, effects.disallowedValues)
        )
      );
    default:
      return false;
  }
};

/**
 * Calls `visit` for each slide parameter in input-group order, with its edited or saved value.
 * `isEdited` is true when the slide's edits hold the key, including an explicit clear.
 */
const forEachSlideParameter = (
  slide: Pick<SlideConfig, 'parameters' | 'inputGroups'> & { key: string },
  currentEdits: Edits,
  currentInitialInputValues: Record<string, Record<string, unknown>>,
  visit: (parameter: SlideConfig['parameters'][number], value: unknown, isEdited: boolean) => void
) => {
  const slideEdits = currentEdits[slide.key] || {};
  const parameterMap = new Map(slide.parameters.map(param => [param.inputId, param]));

  for (const group of slide.inputGroups) {
    for (const inputId of group.inputIds) {
      const parameter = parameterMap.get(inputId);
      if (!parameter) {
        continue;
      }
      const value = resolveParameterValue(
        parameter.key,
        slideEdits,
        currentInitialInputValues[slide.key]
      );
      visit(parameter, value, parameter.key in slideEdits);
    }
  }
};

/**
 * Process the parameter values to use for slide generation or saving config.
 * See if there's an edit for the parameter, and if not, use the initial value.
 * Depending on the type of parameter, we need to handle the value differently.
 */
export const processSlideParameters = (
  slide: SlideConfig & { key: string; duplicateNumber?: number },
  currentEdits: Edits,
  currentInitialInputValues: Record<string, Record<string, unknown>>
) => {
  const parameters: Record<string, unknown> = {};

  forEachSlideParameter(slide, currentEdits, currentInitialInputValues, (parameter, value) => {
    if (value == null || value === '') {
      return;
    }
    parameters[parameter.key] =
      parameter.type === ParameterConfigurationType.SINGLE_SELECT_DROPDOWN &&
      typeof value === 'object' &&
      'value' in value &&
      value.value
        ? value.value
        : value;
  });

  return parameters;
};

/** The editor state that bpm-lambdas `buildSlideDeck` reads (slides/slidesv2/slideDeckState.ts). */
export interface SlideDeckState {
  slides: { key: string; isDisabled: boolean; parameters: Record<string, unknown> }[];
  duplicates: { ofSlideKey: string; isDisabled: boolean; parameters: Record<string, unknown> }[];
}

const DECK_STATE_DATE_FIELDS = ['start', 'end', 'comparisonStart', 'comparisonEnd'];

const isDatePicker = (type: ParameterConfigurationType): boolean =>
  type === ParameterConfigurationType.PRIMARY_ONLY_DATE_PICKER ||
  type === ParameterConfigurationType.PRIMARY_AND_COMPARISON_DATE_PICKER;

const pickDateFields = (value: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    DECK_STATE_DATE_FIELDS.filter(field => value[field] !== undefined).map(field => [
      field,
      value[field]
    ])
  );

const getDeckStateParameters = (
  slide: SlideInstance,
  isDisabled: boolean,
  currentEdits: Edits,
  currentInitialInputValues: Record<string, Record<string, unknown>>
): Record<string, unknown> => {
  const parameters: Record<string, unknown> = {};

  forEachSlideParameter(
    slide,
    currentEdits,
    currentInitialInputValues,
    (parameter, value, isEdited) => {
      const isDate = isDatePicker(parameter.type);
      if (isEdited) {
        // An undefined edit is a clear; JSON drops undefined, and the server reads null as a clear.
        parameters[parameter.key] =
          isDate && isRecord(value) ? pickDateFields(value) : (value ?? null);
      } else if (isDate && !isDisabled && isRecord(value)) {
        parameters[parameter.key] = pickDateFields(value);
      }
    }
  );

  return parameters;
};

/**
 * The editor state to send with an Export. `exportSlides` is the Export payload built from
 * `instances`, index for index, and each entry takes its isDisabled. Every enabled slide sends
 * its dates, so the server does not resolve them with its own clock and time zone.
 */
export const buildDeckState = (
  instances: SlideInstance[],
  exportSlides: Pick<SlideRequest, 'isDisabled'>[],
  currentEdits: Edits,
  currentInitialInputValues: Record<string, Record<string, unknown>>
): SlideDeckState => {
  const deckState: SlideDeckState = { slides: [], duplicates: [] };

  instances.forEach((instance, i) => {
    const isDisabled = Boolean(exportSlides[i].isDisabled);
    const parameters = getDeckStateParameters(
      instance,
      isDisabled,
      currentEdits,
      currentInitialInputValues
    );
    if (instance.source === SlideSource.LOCAL_DUPLICATE) {
      deckState.duplicates.push({
        ofSlideKey: instance.duplicateOfKey,
        isDisabled,
        parameters
      });
    } else if (!deckState.slides.some(slide => slide.key === instance.key)) {
      // Saved loops can share a template key, and the editor keys their state by it.
      deckState.slides.push({ key: instance.key, isDisabled, parameters });
    }
  });

  return deckState;
};

/**
 * Generate a unique local ID for duplicate slides
 */
export const generateLocalId = (): string =>
  `local_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

/**
 * Generates a key for a duplicate slide based on its originalSlideId and localId.
 * A duplicate slide is one that exists for the current session, but is not part of the template.
 */
export const getDuplicateKey = (originalSlideId: string, localId: string): string =>
  `${originalSlideId}_dup_${localId}`;

/**
 * Checks if a slide can be deleted.
 * A slide can be deleted if it's a local duplicate or duplicated from a preset.
 */
export const canDeleteSlide = (source: SlideSource): boolean =>
  // Can delete if it's a local duplicate or preset loop
  source === SlideSource.LOCAL_DUPLICATE || source === SlideSource.PRESET_LOOP;
